import React, { createContext, useContext, useEffect, useState } from "react";
import { api, setCsrf, clearCsrf } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setCsrf(data.csrf_token);
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data && data.mfa_required) return data; // caller drives the MFA step
    setCsrf(data.csrf_token);
    setUser(data);
    return data;
  };

  // Complete a login MFA challenge for an already-enrolled user.
  const mfaChallenge = async (challenge_token, code) => {
    const { data } = await api.post("/auth/mfa/challenge", { challenge_token, code });
    setCsrf(data.csrf_token);
    setUser(data);
    return data;
  };

  const mfaRecovery = async (challenge_token, code) => {
    const { data } = await api.post("/auth/mfa/challenge/recovery", { challenge_token, code });
    setCsrf(data.csrf_token);
    setUser(data);
    return data;
  };

  // Mandatory pre-login enrollment (role requires MFA, not yet enrolled).
  const mfaSetupStart = async (challenge_token) => {
    const { data } = await api.post("/auth/mfa/setup/start", { challenge_token });
    return data;
  };

  const mfaSetupVerify = async (challenge_token, enrollment_id, code) => {
    const { data } = await api.post("/auth/mfa/setup/verify", { challenge_token, enrollment_id, code });
    // Do NOT set the user here: the caller must first show the one-time recovery
    // codes. Setting the user would trigger a route redirect that unmounts the
    // recovery-codes screen before it renders. Session cookie is already set by backend.
    setCsrf(data.user?.csrf_token);
    return data; // { enabled, recovery_codes, user }
  };

  // Activate the authenticated user in app state (called after recovery codes acknowledged).
  const applyAuthenticatedUser = (u) => setUser(u);

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setCsrf(data.csrf_token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    clearCsrf();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh, mfaChallenge, mfaRecovery, mfaSetupStart, mfaSetupVerify, applyAuthenticatedUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
